import React, { useEffect, useState } from 'react';

import { NavigationButtons } from 'composables/HearingTest/components/NavigationButtons';
import { StepContainer } from 'composables/HearingTest/components/StepContainer';

import { VolumeSelector } from '../components/VolumeSelector';
import { useHearingTest, useHearingTestTracking } from '../HearingTestContext';
import { useAudioTest } from '../hooks/useAudioTest';
import { translations } from '../translations/en';
import { FREQUENCIES, TOTAL_STEPS } from '../types/HearingTestTypes';
import { HearingTestEventAction } from '../types/TrackingTypes';

interface FrequencyTestStepProps {
  frequency: FREQUENCIES;
  ear: 'left' | 'right';
}

export const FrequencyTestStep: React.FC<FrequencyTestStepProps> = ({ frequency, ear }) => {
  const { goToStep, state, saveFrequencyScore } = useHearingTest();
  const { trackHearingTestEvent } = useHearingTestTracking();
  const { loadAudio, playTone, stopTone, cleanup, isPlaying, error } = useAudioTest();

  const [selectedVolume, setSelectedVolume] = useState<number | null>(null);
  const [audioLoaded, setAudioLoaded] = useState(false);

  const frequencyLabel = translations[`${frequency}hz${ear === 'left' ? 'l' : 'r'}`];

  useEffect(() => {
    const earLabel = ear === 'left' ? 'Left' : 'Right';
    const freqValue = frequency.toString().replace('hz', '');
    trackHearingTestEvent(HearingTestEventAction.LoadPage, `${earLabel} ${freqValue}Hz Page`);
  }, [ear, frequency, trackHearingTestEvent]);

  useEffect(() => {
    setSelectedVolume(null);
    setAudioLoaded(false);
  }, [frequency, ear]);

  useEffect(() => {
    const initAudio = async () => {
      try {
        await loadAudio(frequency);
        setAudioLoaded(true);
      } catch (error) {
        console.error('Failed to load audio:', error);
      }
    };

    void initAudio();
    return () => stopTone();
  }, [frequency, loadAudio, stopTone]);

  useEffect(() => {
    return () => {
      cleanup().catch((error) => {
        console.error('Error cleaning up audio context:', error);
      });
    };
  }, [cleanup]);

  const handleVolumeSelect = async (level: number) => {
    setSelectedVolume(level);

    if (!audioLoaded) {
      try {
        await loadAudio(frequency);
        setAudioLoaded(true);
      } catch (error) {
        console.error('Failed to load audio on user interaction:', error);
        return;
      }
    }

    if (isPlaying) stopTone();
    try {
      await playTone(level, ear);
    } catch (error) {
      console.error('Failed to play tone:', error);
    }
  };

  const handleSaveAndContinue = () => {
    if (selectedVolume === null) return;

    const earLabel = ear === 'left' ? 'Left' : 'Right';
    const freqValue = frequency.toString().replace('hz', '');
    const scoreLabel = `${earLabel} ${freqValue}Hz Score`;

    trackHearingTestEvent(scoreLabel, selectedVolume);

    stopTone();
    saveFrequencyScore(ear, frequency, selectedVolume);
    goToStep(state.currentStep + 1);
  };
  const canProceed = selectedVolume !== null;
  const volumeError = canProceed ? null : translations.volumehelp;

  return (
    <StepContainer currentStep={state.currentStep} totalSteps={TOTAL_STEPS}>
      <div className="space-y-8">
        <div className="text-center">
          <p className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-700">
            {translations.maxvolheadphoneson}
          </p>
          <h1
            className="mb-4 text-3xl font-bold"
            dangerouslySetInnerHTML={{ __html: frequencyLabel }}
          />
          <p
            className="text-lg text-gray-700"
            dangerouslySetInnerHTML={{ __html: translations.clicknumbers }}
          />
        </div>

        {error && (
          <div className="rounded border border-error-400 bg-error-100 px-4 py-3 text-error-700">
            <p className="font-bold">Audio Error</p>
            <p className="text-sm">{error.message}</p>
          </div>
        )}

        <div className="max-lg:pt-8">
          <VolumeSelector
            key={`${frequency}-${ear}`}
            onVolumeSelect={handleVolumeSelect}
            selectedVolume={selectedVolume}
            ear={ear}
          />
          {volumeError && (
            <p className="mt-2 text-sm text-error-600 md:text-right">{volumeError}</p>
          )}
        </div>

        <NavigationButtons onNext={handleSaveAndContinue} disableNext={!canProceed} />
      </div>
    </StepContainer>
  );
};
